
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Requirement } from "@/types/project";

interface DocumentUploadDialogProps {
  requirement: Requirement;
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function DocumentUploadDialog({ 
  requirement, 
  projectId, 
  isOpen, 
  onClose, 
  onSuccess 
}: DocumentUploadDialogProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<string>("compliance_evidence");

  const documentTypes = [
    { id: "compliance_evidence", name: "Compliance Evidence" },
    { id: "certification", name: "Certification" },
    { id: "test_results", name: "Test Results" },
    { id: "declaration", name: "Declaration" },
    { id: "other", name: "Other" }
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    try {
      setIsUploading(true);

      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // 1. Create a unique path for the file in storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${projectId}/${requirement.id}/${fileName}`;

      // 2. Upload the file to Supabase Storage using the correct bucket name
      const { error: uploadError } = await supabase.storage
        .from('stdly-project-documents')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // 3. Create the document record in the database
      // Use requirement.requirement_id if available, otherwise fall back to requirement.id
      const requirementIdToUse = requirement.requirement_id || requirement.id;
      
      const { error: insertError } = await supabase
        .from('requirement_documents')
        .insert({
          project_id: projectId,
          requirement_id: requirementIdToUse,
          document_type: documentType,
          file_path: filePath,
          file_name: file.name,
          uploaded_by: user.id,
          status: 'queued' // Set initial status as queued
          // user_id is set by default in the database
        });

      if (insertError) {
        throw insertError;
      }

      toast.success("Document uploaded successfully and queued for assessment");
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error uploading document:", error);
      toast.error(error.message || "Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Documentation</DialogTitle>
          <DialogDescription>
            Upload documentation for requirement: {requirement.identifier || ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="documentType">Document Type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger>
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {documentTypes.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="file">Document File</Label>
            <Input 
              id="file" 
              type="file" 
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <p className="text-sm text-muted-foreground">
              Accepted file types: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
            </p>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-md border border-blue-100 text-sm text-blue-800">
            <p>Documents will be queued for assessment after upload. You'll be able to see the assessment results once processing is complete.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={isUploading || !file}>
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
